// Logique PURE du formulaire public d'auto-inspection « par étapes ».
//
// Une ÉTAPE = une ou plusieurs SECTIONS consécutives de la grille portant le même
// champ `etape` (une section sans `etape` forme sa propre étape). Les réponses sont
// stockées au format NATIF des inspections (miroir de services/auto_declaration.py) :
//   section non répétable : section_{s}_item_{i}
//   section répétable     : section_{s}_inst_{K}_item_{i}
import { evaluerConditionsAffichage, valeursPourInstance } from './conditionsAffichage';

export const TYPES_SANS_REPONSE = new Set(['info', 'titre', 'section', 'signature']);
export const VALEUR_PHOTO = 'Photo jointe';

export const itemsDe = (section) => {
  const arr = section?.items || section?.questions || [];
  return Array.isArray(arr) ? arr : [];
};

export const cleItem = (s, i, instance = null) => (
  instance === null || instance === undefined
    ? `section_${s}_item_${i}`
    : `section_${s}_inst_${instance}_item_${i}`
);

/** Regroupe les sections consécutives de même `etape` : [{ titre, sections: [indices] }]. */
export const regrouperEtapes = (sections) => {
  const etapes = [];
  (sections || []).forEach((section, s) => {
    const titre = section?.etape || section?.titre || `Étape ${etapes.length + 1}`;
    const derniere = etapes[etapes.length - 1];
    if (derniere && section?.etape && derniere.titre === section.etape) {
      derniere.sections.push(s);
    } else {
      etapes.push({ titre, sections: [s] });
    }
  });
  return etapes;
};

/** Valeur effective : la réponse, sinon la valeur par défaut de l'item (ex. « Propane »). */
export const valeurDe = (item, reponses, cle) => {
  const v = reponses[cle];
  if ((v === undefined || v === null || v === '') && item?.valeur_defaut) return item.valeur_defaut;
  return v;
};

/** id d'item → valeur (clés plates), sur toute la grille — pour les conditions. */
export const valeursPlates = (sections, reponses) => {
  const v = {};
  (sections || []).forEach((section, s) => {
    itemsDe(section).forEach((it, i) => {
      if (it && typeof it === 'object' && it.id) v[it.id] = valeurDe(it, reponses, cleItem(s, i));
    });
  });
  return v;
};

/**
 * Parcourt les questions VISIBLES de la section `s` : appelle `visiteur(item, cle, instance, i)`.
 * Une section répétable est parcourue pour chacune de ses `nbInstances` instances.
 */
export const parcourirSection = (sections, s, reponses, nbInstances, plates, visiteur) => {
  const section = sections[s];
  if (!section || !evaluerConditionsAffichage(section.conditions_affichage, plates)) return;
  const items = itemsDe(section);
  const instances = section.est_repetable
    ? Array.from({ length: nbInstances || 0 }, (_, k) => k)
    : [null];
  instances.forEach((instance) => {
    const valeurs = instance === null
      ? plates
      : valeursPourInstance(plates, items, s, instance, reponses);
    items.forEach((item, i) => {
      if (!item || typeof item !== 'object') return;
      if (!evaluerConditionsAffichage(item.conditions_affichage, valeurs)) return;
      visiteur(item, cleItem(s, i, instance), instance, i);
    });
  });
};

const estVide = (v) => v === undefined || v === null || v === ''
  || (Array.isArray(v) && v.length === 0);

/** Clés des questions obligatoires VISIBLES et vides des sections données. */
export const champsManquants = (sections, indicesSections, reponses, instances) => {
  const plates = valeursPlates(sections, reponses);
  const manquants = [];
  indicesSections.forEach((s) => {
    parcourirSection(sections, s, reponses, instances[s], plates, (item, cle) => {
      if (TYPES_SANS_REPONSE.has(item.type) || !item.obligatoire) return;
      if (estVide(valeurDe(item, reponses, cle))) manquants.push(cle);
    });
  });
  return manquants;
};

/**
 * Réponses à transmettre : seulement celles des questions VISIBLES (une réponse
 * masquée est effacée), valeurs par défaut comprises. Point fixe : une valeur
 * masquée ne doit pas rendre visible une autre question.
 */
export const reponsesVisibles = (sections, reponses, instances) => {
  let courantes = reponses || {};
  for (let tour = 0; tour < 10; tour += 1) {
    const plates = valeursPlates(sections, courantes);
    const gardees = {};
    (sections || []).forEach((_, s) => {
      parcourirSection(sections, s, courantes, instances[s], plates, (item, cle) => {
        if (TYPES_SANS_REPONSE.has(item.type)) return;
        const v = valeurDe(item, courantes, cle);
        if (!estVide(v)) gardees[cle] = v;
      });
    });
    const stable = Object.keys(gardees).length === Object.keys(courantes).length
      && Object.keys(gardees).every((k) => gardees[k] === courantes[k]);
    courantes = gardees;
    if (stable) break;
  }
  return courantes;
};

/** Articles déclenchés par la réponse courante (alertes de l'item). */
export const articlesDeclenches = (item, valeur) => {
  if (estVide(valeur)) return [];
  const vals = (Array.isArray(valeur) ? valeur : [valeur]).map(String);
  const arts = [];
  (item?.alertes || []).forEach((a) => {
    const decls = (a?.declencheurs || []).map(String);
    if (decls.length && vals.some((v) => decls.includes(v))) {
      (a.articles || []).forEach((art) => { if (art) arts.push(art); });
    }
  });
  return arts;
};

/** Retire l'instance `k` d'une section répétable et décale les suivantes. */
export const retirerInstance = (reponses, s, k, nb) => {
  const prefixe = `section_${s}_inst_`;
  const res = {};
  Object.entries(reponses).forEach(([cle, v]) => {
    if (!cle.startsWith(prefixe)) { res[cle] = v; return; }
    const m = cle.slice(prefixe.length).match(/^(\d+)_item_(\d+)$/);
    if (!m) { res[cle] = v; return; }
    const inst = parseInt(m[1], 10);
    if (inst === k || inst >= nb) return;
    res[inst > k ? `${prefixe}${inst - 1}_item_${m[2]}` : cle] = v;
  });
  return res;
};

/** (999) 999-9999 à partir des chiffres saisis. */
export const formaterTelephone = (saisie) => {
  const d = String(saisie || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export const courrielValide = (c) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(c || '').trim());

/** Alertes de l'item déclenchées par la réponse (pour afficher intro + articles). */
export const alertesDeclenchees = (item, valeur) => {
  if (estVide(valeur)) return [];
  const vals = (Array.isArray(valeur) ? valeur : [valeur]).map(String);
  return (item?.alertes || []).filter((a) => {
    const decls = (a?.declencheurs || []).map(String);
    return decls.length > 0 && vals.some((v) => decls.includes(v));
  });
};

// Date saisie en 3 cases JJ / MM / AAAA, stockée « AAAA-MM-JJ » (format des inspections).
export const partiesDate = (v) => {
  const [a = '', m = '', j = ''] = String(v || '').split('-');
  return { a, m, j };
};
export const composerDate = ({ a, m, j }) => ((a || m || j) ? `${a}-${m}-${j}` : '');
export const dateComplete = (v) => {
  const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ''));
  if (!r) return false;
  const d = new Date(Date.UTC(+r[1], +r[2] - 1, +r[3]));
  return d.getUTCFullYear() === +r[1] && d.getUTCMonth() === +r[2] - 1 && d.getUTCDate() === +r[3];
};

/** Clés des dates VISIBLES commencées mais incomplètes ou impossibles (ex. 31/02). */
export const datesInvalides = (sections, indicesSections, reponses, instances) => {
  const plates = valeursPlates(sections, reponses);
  const invalides = [];
  indicesSections.forEach((s) => {
    parcourirSection(sections, s, reponses, instances[s], plates, (item, cle) => {
      const v = reponses[cle];
      if (item.type === 'date' && !estVide(v) && !dateComplete(v)) invalides.push(cle);
    });
  });
  return invalides;
};
